import React from 'react';
import { Grid, Box, Text, Button, Card, CardHeader, DropButton, Image } from 'grommet';
import { Navigate } from 'grommet-icons';

// Import the grid data
import gridData from '../../data/roadmaps/NorthStarGridData.json';

export const NorthStarGrid = () => {
  const rowLabels = gridData['rowLabels']; // The row labels (e.g., "Value Chain", "L2 Platforms", etc.)
  const l1Items = gridData.L1.items; // The L1 items (each with an array of L2 items)
  const columnCount = gridData.maxL2Columns;
  const corePlatforms = gridData.corePlatforms;

  // Define rows dynamically based on rowLabels length and adding the header row
  const rows = ['auto', 'auto', ...Array(rowLabels.length - 1).fill('auto'), 'auto']; // Add an extra row for the footer

  // Define columns: First column for row labels, then dynamically adjust for L1 items
  const columns = [
    'auto', // Column for row labels
    ...l1Items.map(() => 'auto') // Use auto for each column based on L2 content
  ];

  return (
    <Box 
      width="100%"  
      pad={{ top: 'medium', bottom: 'medium' }} 
      style={{
        display: 'flex',  // Use flexbox to expand and fill available space
        flexDirection: 'column',  // Ensure the layout is vertical (column-wise)
        overflowX: 'auto',
        minWidth: '100%',
        backgroundImage: 'url(/images/north-star-triangle.png)', 
        backgroundSize: 'contain',  
        backgroundPosition: 'calc(50% + 45px) top', 
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'local', 
      }}    
    >
      <Grid
        rows={rows}
        columns={columns}
        gap="xxsmall"
        pad={{ top: 'xsmall' }}
        style={{ minWidth: '1800px' }} // Ensure grid has enough width to trigger scrollbar when content overflows
      >
        {/* Render the first row (header row) with the L1 items as column headers */}
        {l1Items.map((item, colIndex) => (
          <Box
            key={item.label}
            gridArea={`1 / ${colIndex + 2} / 2 / ${colIndex + 2}`} // Header in row 1, starting from column 2
            align="center"
            justify="center"
            background="brand"
            height="50px"
          >
            <DropButton
              label={<Text weight="bold" size="small">{item.label}</Text>} // L1 label as text
              fill={true}
              width="fit-content"
              dropContent={
                <Box pad="small" background="light-1" width="100%" margin="0" style={{ display: 'flex', flexDirection: 'column' }}>
                  <Button label="Option 1" onClick={() => console.log(`${item.label} Option 1 selected`)} />
                  <Button label="Option 2" onClick={() => console.log(`${item.label} Option 2 selected`)} />
                  <Button label="Option 3" onClick={() => console.log(`${item.label} Option 3 selected`)} />
                </Box>
              }
              dropProps={{
                align: { top: 'bottom', left: 'left' },
                elevation: 'small',
              }}
              pad="none" // Remove extra padding around the button
              style={{ width: '100%', height: '100%' }} // Ensure button takes up the full space and maintains header's size
            />
          </Box>
        ))}

        {/* Render the L2 items dynamically using a Grommet Grid */}
        {l1Items.map((item, colIndex) => {
          const l2GridColumnCount = Math.min(item.L2.length, columnCount);

          return (
            <Box
              key={item.label}
              gridArea={`2 / ${colIndex + 2} / 3 / ${colIndex + 2}`} // L2 items go in row 2 under the respective L1 columns
              align="start"
              justify="start"
              pad="xxsmall"
              background="light-1"
            >
              <Grid
                rows={['auto', 'auto']} 
                columns={Array(l2GridColumnCount).fill('1fr')} 
                gap="xsmall"
              >
                {item.L2.map((l2, index) => (
                  <Box
                    key={l2.label}
                    style={{
                      width: '100%', 
                      marginBottom: '8px',
                    }}
                  >
                    <Card width="100%" elevation="small" margin="none">
                      <CardHeader
                        align="start"
                        justify="between"
                        direction="row"
                        pad={{ left: 'xsmall', right: 'xsmall', top: 'small', bottom: 'small' }}
                      >
                        <Box flex={true} align="start" pad="none">
                          <Text weight="bold" size="xsmall">{l2.label}</Text>
                        </Box>
                        <Box align="start" justify="start" pad="none" height="full">
                          <DropButton
                            icon={<Navigate size="small" color="brand" />}
                            pad="none"
                            size="xsmall"
                            dropContent={
                              <Box pad="small" background="light-1" width="100%" margin="0" style={{ display: 'flex', flexDirection: 'column' }}>
                                <Button label="Option 1" onClick={() => console.log('Option 1 selected')} />
                                <Button label="Option 2" onClick={() => console.log('Option 2 selected')} />
                                <Button label="Option 3" onClick={() => console.log('Option 3 selected')} />
                              </Box>
                            }
                            dropProps={{
                              align: { top: 'bottom', left: 'left' },
                              elevation: 'small',
                            }}
                          />
                        </Box>
                      </CardHeader>

                      <Box direction="column" gap="xsmall" pad={{ left: 'xsmall', right: 'xsmall', bottom: 'small' }}>
                        {l2.platforms.map((platform) => (
                          <DropButton
                            key={platform}
                            label={platform}
                            secondary
                            size="xsmall"
                            dropContent={
                              <Box pad="small" background="light-1" width="100%" margin="0" style={{ display: 'flex', flexDirection: 'column' }}>
                                <Button label={`Option for ${platform}`} onClick={() => console.log(`${platform} Option 1 selected`)} />
                                <Button label="Option 2" onClick={() => console.log(`${platform} Option 2 selected`)} />
                                <Button label="Option 3" onClick={() => console.log(`${platform} Option 3 selected`)} />
                              </Box>
                            }
                            dropProps={{
                              align: { top: 'bottom', left: 'left' },
                              elevation: 'small',
                            }}
                          />
                        ))}
                      </Box>
                    </Card>
                  </Box>
                ))}
              </Grid>
            </Box>
          );
        })}

        {/* Row 3 corePlatforms data... */}
        <Box
          gridArea={`3 / 2 / 4 / ${columns.length + 1}`} // Start from row 3, column 2, span all columns
          direction="row"
          gap="small"
          pad="small"
          justify="center"
          background="teal"
        >
          {/* Add corePlatforms dynamically */}
          {corePlatforms.map((platform) => (
            <DropButton
              key={platform.name}  // Ensure platform is an object with 'name' and 'logo'
              label={null} // We do not need a label here as we're using the card for the button.
              elevation="medium" 
              dropContent={
                <Box pad="small" background="light-1" width="100%" margin="0">
                  <Button label={`Option for ${platform.name}`} onClick={() => console.log(`Option for ${platform.name} selected`)} />
                  <Button label="Option 2" onClick={() => console.log(`Option 2 for ${platform.name} selected`)} />
                  <Button label="Option 3" onClick={() => console.log(`Option 3 for ${platform.name} selected`)} />
                </Box>
              }
              dropProps={{
                align: { top: 'bottom', left: 'left' },
                elevation: 'small',
              }}
            >
              <Card width="150px" elevation="small">
                <CardHeader align="center" justify="center" pad="small">
                  <Box direction="column" align="center" justify="center">
                    <Image 
                      src={platform.logo} 
                      alt={platform.name} 
                      style={{ maxHeight: '30px', marginBottom: '2px' }}
                    />
                    <Text weight="bold" size="medium" textAlign="center">{platform.name}</Text>
                  </Box>
                </CardHeader>
              </Card>
            </DropButton>
          ))}
        </Box>

        {/* Row 4 coreIT data... */}
        <Box
          gridArea={`4 / 2 / 4 / ${columns.length + 1}`} // Start from row 4, column 2, span all columns
          direction="column" 
          pad="xsmall" 
          gap="xsmall" 
          align="center"
          background="orange"
        >
          {/* First two development value stream boxes... */}
          <Box direction="row" gap="medium" width="100%" justify="center">
            <Box
              onClick={() => console.log('Button 1 clicked')}
              background="light-2"
              border={{ color: 'grey', size: 'small' }}
              round="small"
              style={{ width: '17%', height: '22px', marginLeft: '4%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Vertically center text
            >
              <Text size="xsmall" style={{ fontStyle: 'italic' }}>Development Value Stream 1</Text>
            </Box>
            <Box
              onClick={() => console.log('Button 2 clicked')}
              background="light-2"
              border={{ color: 'grey', size: 'small' }}
              round="small"
              margin={{ left: 'xlarge'}}
              style={{ width: '40%', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Vertically center text
            >
              <Text size="xsmall" style={{ fontStyle: 'italic' }}>Development Value Stream 2</Text>
            </Box>
            <Box alignSelf="end" pad={{ left: 'none' }} margin={{ left: '-15px' }} style={{ height: '22px' }}>
              <Text size="large" textAlign="bottom" weight="bold" pad="none" margin="none">...</Text> 
            </Box>    
          </Box>

          {/* Third development value stream box... */}
          <Box direction="row" width="100%" justify="center" gap="xsmall" margin={{ top: 'none' }} pad={{ top: 'none' }}> 
            <Box
              onClick={() => console.log('Button 3 clicked')}
              background="light-2"
              border={{ color: 'grey', size: 'small' }}
              round="small"
              gap="xsmall"
              margin={{ top: 'none' }}
              style={{ width: '30%', height: '22px', marginLeft: '5%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} // Vertically center text
            >
              <Text size="xsmall" style={{ fontStyle: 'italic' }}>Development Value Stream 3</Text>
            </Box>
            <Box alignSelf="end">
              <Text size="large" textAlign="bottom" weight="bold">...</Text> 
            </Box>
          </Box>

          {/* Core IT boxes below the buttons */} 
          <Box
            direction="row"
            gap="small"
            pad="xsmall"
            justify="center"
            background="orange"
            wrap
          >
            {/* Add coreIT items dynamically */}
            {gridData.coreIT.map((IT) => (
              <DropButton
                key={IT}  // Since IT is just a string (e.g., "IT4IT"), using IT directly as key
                label={null} // We do not need a label here as we're using the card for the button.
                elevation="medium" 
                background="background"
                dropContent={
                  <Box pad="small" background="light-1" width="100%" margin="0">
                    <Button label={`Option for ${IT}`} onClick={() => console.log(`Option for ${IT} selected`)} />
                    <Button label="Option 2" onClick={() => console.log(`Option 2 for ${IT} selected`)} />
                    <Button label="Option 3" onClick={() => console.log(`Option 3 for ${IT} selected`)} />
                  </Box>
                }
                dropProps={{
                  align: { top: 'bottom', left: 'left' },
                  elevation: 'small',
                }}
              >
                <Card width="150px" elevation="medium">
                  <CardHeader align="center" justify="center" pad="small">
                    <Text weight="bold" size="medium">{IT}</Text>
                  </CardHeader>
                </Card>
              </DropButton>
            ))}
          </Box>
        </Box>

        {/* Add a new row at the bottom with a centered label */}
        <Box
          gridArea={`5 / 1 / 6 / ${columns.length + 1}`} // Span all columns
          background="brand"
          align="center"
          justify="center"
          pad="small"
        >
          <Text size="medium" weight="bold" color="white">
            {"<-- Native AI and Security -->"}
          </Text>
        </Box>

        {/* Render the row labels in the first column of each row */}
        {rowLabels.map((label, rowIndex) => (
          <Box
            key={label}
            gridArea={`${rowIndex + 1} / 1 / ${rowIndex + 2} / 2`} // Row labels go in the first column
            align="center"
            justify="center"
            background={rowIndex === 0 ? 'brand' : 'blue!'}
            style={{
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              width: '85px',
            }}
          >
            <Text textAlign="center" weight="bold" size="small" pad="small">{label}</Text>
          </Box>
        ))}
      </Grid>
    </Box>
  );
};

